import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import settings from '/@/core/settings';
import { revalidateMyFields } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { setField } from '/@/store/crop-monitoring/actions';
import { getCropMonitoringById } from '/@/store/crop-monitoring/apiProviders';
import {
    setCropMonitoring,
    setEstimatedApplicationDate,
    setNitrogenSource,
    setPlantingDate,
    setProductName,
    setSelectedHybrid,
} from '/@/store/nitrogen-recommendation/actions';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import { cropMonitoringPendingAsyncState, getCropMonitoringData } from '/@/utils/crop-monitoring';
import { invalidate } from '/@/utils/swr';

const CM_PENDING_ASYNC_POLL_MS = 5_000;

export async function getCropMonitoring(cropMonitoringId: number) {
    const res = await axFetch.get(`${settings.urls.apiDomain}/core/v1/crop-monitoring/${cropMonitoringId}/`);
    return Promise.resolve(res);
}

export const getAndSetCropMonitoringOnNitrogenSmartRedux = async (dispatch, cropMonitoringId) => {
    const { data } = await getCropMonitoring(cropMonitoringId);
    const cropData = getCropMonitoringData(data);
    dispatch(setField(data.field));
    dispatch(setCropMonitoring(data));
    dispatch(setPlantingDate(data.plantingDate));
    dispatch(setNitrogenSource(cropData?.fertilizer));
    dispatch(setSelectedHybrid(cropData?.product));
    dispatch(setProductName(cropData?.productName));
    if (data.expectedV6Date) {
        dispatch(setEstimatedApplicationDate(cropData?.expectedV6Date));
    }

    return data;
};

/**
 * @typedef {Object} CropMonitoring
 * @property {number} id
 * @property {string} field
 * @property {any[]} stages
 * @property {number} user
 * @property {string} currentStage
 * @property {number} currentStagePercentage
 * @property {object} vegetativeStage
 * @property {any[]} nitrogenRecomendations
 * @property {string} created
 * @property {string} modified
 * @property {object} deleted_at
 * @property {string} plantingDate
 * @property {number} humidity
 * @property {number} environment
 * @property {number} density
 * @property {number} fertilizer_amount
 * @property {number} accumulatedGdu
 * @property {object} accumulatedGduDate
 * @property {object} expectedFloweringDate
 * @property {object} expectedMaturityDate
 * @property {boolean} active
 * @property {number} appInstance
 * @property {number} product
 * @property {number} fertilizer
 */

export const useCropMonitoringById = (id: number) => {
    const key = id ? { key: 'useCropMonitoringById', id } : null;
    const [refreshIntervalMs, setRefreshIntervalMs] = useState(0);

    const cropMonitoring = useSWR(key, () => getCropMonitoringById(id), {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        refreshInterval: refreshIntervalMs,
    });

    useEffect(() => {
        if (!id) {
            setRefreshIntervalMs(0);
            return;
        }
        setRefreshIntervalMs(cropMonitoringPendingAsyncState(cropMonitoring.data) ? CM_PENDING_ASYNC_POLL_MS : 0);
    }, [id, cropMonitoring.data]);

    return cropMonitoring;
};

/**
 * Invalidates all crop monitoring queries and related fields queries
 * @param cmId - The crop monitoring ID to invalidate
 * @param fieldId - The field ID to invalidate
 * @param country - The country code for field queries invalidation
 */
export const invalidateCropMonitoringQueries = ({
    cmId,
    fieldId,
    country,
}: {
    cmId?: number;
    fieldId: number;
    country: string;
}) => {
    // Invalidate specific CM if provided
    if (cmId) {
        invalidate({ key: 'useCropMonitoringById', id: cmId });
    }
    // Invalidate all CMs list
    invalidate({ key: 'allCropMonitorings' });
    // Invalidate specific field
    invalidate({ key: 'useFieldById', fieldId });
    // Invalidate all fields queries
    revalidateMyFields({ country, fieldId });
    // Invalidate all dashboard queries
    invalidate({ key: 'growersDashboardReports' });
};

export const useMutateArchiveCropMonitoringById = (id: number) => {
    const country = useSelector(getSessionCountry);
    const key = id ? { key: 'useMutateArchiveCropMonitoringById', id } : null;
    return useSWRMutation(key, async ({ id }) => {
        const res = await axFetch.patch(`${settings.urls.apiDomain}/core/v1/crop-monitoring/${id}/`, { active: false });
        invalidateCropMonitoringQueries({
            cmId: id,
            fieldId: res.data.field,
            country: country.code,
        });
        return res.data;
    });
};

export const useMutateSetHarvestDateCropMonitoringById = (id: number) => {
    const country = useSelector(getSessionCountry);
    const key = id ? { key: 'useMutateSetHarvestDateCropMonitoringById', id } : null;
    return useSWRMutation(
        key,
        async ({ id }) => {
            const today = new Date().toISOString().split('T')[0];
            const res = await axFetch.patch(`${settings.urls.apiDomain}/core/v1/crop-monitoring/${id}/`, {
                harvest_date: today,
            });
            invalidateCropMonitoringQueries({
                cmId: id,
                fieldId: res.data.field,
                country: country.code,
            });
            return res.data;
        },
        {
            onSuccess: () => {
                enqueueSnackbar(_('¡Tu seguimiento de cultivo se ha finalizado con éxito!'), {
                    variant: 'success',
                });
            },
            onError: () => {
                enqueueSnackbar(
                    _('Ocurrió un error al finalizar tu seguimiento de cultivo. Por favor, inténtalo más tarde.'),
                    {
                        variant: 'error',
                    }
                );
            },
        }
    );
};

export const useMutateCreateOrUpdateCropMonitoring = (
    {
        id,
        plantingDate,
        environment,
        plantedArea,
        density,
        field,
        product,
        technology,
        crop,
        previousCrop,
        sourcePlantingReport,
    }: {
        id?: number;
        plantingDate?: string;
        environment?: number;
        plantedArea?: number;
        density?: number;
        field: number;
        product?: number | string;
        technology?: number;
        crop?: string;
        previousCrop?: string;
        /** FieldView `PlantingReportRequest` id when the form was prefilled from a merged planting report. */
        sourcePlantingReport?: number;
    },
    {
        onSuccess,
    }: {
        onSuccess?: (data: any) => void;
    }
) => {
    const appInstance = useSelector(getAppInstance)?.id;
    const country = useSelector(getSessionCountry);
    const key =
        plantingDate && environment && field && product !== undefined
            ? {
                  id,
                  plantingDate,
                  environment,
                  plantedArea,
                  density,
                  field,
                  product,
                  technology,
                  crop,
                  previousCrop,
                  sourcePlantingReport,
                  key: 'useMutateCreateOrUpdateCropMonitoring',
              }
            : null;

    return useSWRMutation(
        key,
        async ({
            id,
            plantingDate,
            environment,
            plantedArea,
            density,
            field,
            product,
            technology,
            crop,
            previousCrop,
            sourcePlantingReport,
        }) => {
            const res = await axFetch.put(`${settings.urls.apiDomain}/core/v1/crop-monitoring/`, {
                plantingDate: dayjs(plantingDate).format('YYYY-MM-DD'),
                environment: environment,
                plantedArea: plantedArea ?? null,
                density: density ?? null,
                field: field,
                product: product === -1 ? null : product,
                technology: technology,
                crop: crop,
                previous_crop: previousCrop || null,
                ...(sourcePlantingReport != null ? { sourcePlantingReport } : {}),
                appInstance: appInstance,
                id,
            });

            // If updating and the ID changed, invalidate the old one too
            if (id && id !== res.data.id) {
                invalidateCropMonitoringQueries({
                    cmId: id,
                    fieldId: res.data.field,
                    country: country.code,
                });
            }

            // Invalidate all queries for the new/updated CM
            invalidateCropMonitoringQueries({
                cmId: res.data.id,
                fieldId: res.data.field,
                country: country.code,
            });
            return res.data;
        },
        {
            onSuccess: (data) => {
                onSuccess?.(data);
            },
            onError: () => {
                enqueueSnackbar(_('Error al agregar el cultivo'), { variant: 'error' });
            },
        }
    );
};
